import type {
  FilterField,
  PatternValue,
  RoutingPattern,
} from '../../types/integrations';

export type PatternOperator =
  | 'equals'
  | 'one-of'
  | 'prefix'
  | 'contains'
  | 'wildcard'
  | 'exists'
  | 'anything-but'
  | 'numeric';

export type NumericComparator = '>' | '>=' | '<' | '<=' | '=';

/** Full operator set. The deployed provider catalog doesn't ship per-field
 *  type metadata yet, so we offer every operator on every field. */
export const ALL_OPERATORS: PatternOperator[] = [
  'equals',
  'one-of',
  'prefix',
  'contains',
  'wildcard',
  'exists',
  'anything-but',
  'numeric',
];

/** A single row in the rule builder UI. */
export interface FieldFilterRow {
  /** Local UID for React keys; not sent to the backend. */
  id: string;
  fieldPath: string;
  operator: PatternOperator;
  /**
   * Shape depends on operator:
   * - `equals`           → string | number | boolean
   * - `one-of`           → Array<string | number | boolean>
   * - `prefix`           → string
   * - `contains`         → string (serialized as a `*value*` wildcard)
   * - `wildcard`         → string (raw pattern, `*` = any run of characters)
   * - `exists`           → boolean
   * - `anything-but`     → Array<string | number>
   * - `numeric`          → { op: NumericComparator; n: number }
   */
  value: unknown;
  /**
   * Set only for rows whose field is `keyed` (e.g. Datadog `tagMap`): the map
   * key this row constrains. The leaf is written one level below `fieldPath`,
   * so two rows on the same field with different keys AND together.
   */
  tagKey?: string;
}

/** Stable id generator for rows. Not cryptographically meaningful. */
export function newRowId(): string {
  return `row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

const TOP_LEVEL_EVENT_TYPE_KEY = 'eventType';

/**
 * Set a nested object value at the given dot-path. Mutates `target` and
 * returns it for chaining. Intermediate objects are created as needed.
 */
function setAtPath(target: Record<string, unknown>, path: string, leaf: PatternValue): void {
  const parts = path.split('.').filter(Boolean);
  if (parts.length === 0) return;
  let cursor: Record<string, unknown> = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const next = cursor[key];
    if (next && typeof next === 'object' && !Array.isArray(next)) {
      cursor = next as Record<string, unknown>;
    } else {
      // Overwrite a primitive/array sitting at a path that needs to become
      // an object. Backend pattern shape requires nested objects up to the
      // leaf — there's no ambiguous "two values at the same path" case.
      const fresh: Record<string, unknown> = {};
      cursor[key] = fresh;
      cursor = fresh;
    }
  }
  cursor[parts[parts.length - 1]] = leaf;
}

/** Read the value at a dot-path, or undefined if the path doesn't exist. */
function getAtPath(source: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.').filter(Boolean);
  let cursor: unknown = source;
  for (const key of parts) {
    if (!cursor || typeof cursor !== 'object' || Array.isArray(cursor)) return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

const OPERATOR_KEYS = new Set(['prefix', 'wildcard', 'exists', 'anything-but', 'numeric']);

/** True when an object is an EventBridge operator wrapper, not a nested envelope. */
function isOperatorLeaf(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value as object);
  // Operator wrappers always have exactly one key drawn from OPERATOR_KEYS.
  return keys.length === 1 && OPERATOR_KEYS.has(keys[0]);
}

/**
 * Walk an object and collect every dot-path that points at a leaf. Arrays are
 * treated as leaves (they encode OR-of-values). Operator wrapper objects like
 * `{ prefix: 'PROJ-' }` are also leaves — recursing into them would invent
 * paths like `…projectKey.prefix` that don't exist on the event itself.
 */
function collectLeafPaths(source: Record<string, unknown>, prefix = ''): string[] {
  const out: string[] = [];
  for (const [key, value] of Object.entries(source)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !isOperatorLeaf(value)
    ) {
      out.push(...collectLeafPaths(value as Record<string, unknown>, path));
    } else {
      out.push(path);
    }
  }
  return out;
}

function leafForRow(row: FieldFilterRow): PatternValue {
  switch (row.operator) {
    case 'equals':
      // EventBridge: single-value match still wraps in an array — same payload
      // semantics, but easier to round-trip parse.
      return [row.value as string | number | boolean];
    case 'one-of':
      return (row.value as Array<string | number | boolean>) ?? [];
    case 'prefix':
      return { prefix: String(row.value ?? '') };
    case 'contains':
      // "contains" is a presentation of wildcard, not a separate backend
      // operator — anchored patterns mean `*x*` is exactly substring match.
      return { wildcard: `*${String(row.value ?? '')}*` };
    case 'wildcard':
      return { wildcard: String(row.value ?? '') };
    case 'exists':
      return { exists: Boolean(row.value) };
    case 'anything-but':
      return { 'anything-but': (row.value as Array<string | number>) ?? [] };
    case 'numeric': {
      const v = (row.value ?? {}) as { op?: NumericComparator; n?: number };
      return { numeric: [v.op ?? '=', Number(v.n ?? 0)] };
    }
  }
}

/**
 * Build a routing pattern from the rule builder UI state.
 *
 * - Every row contributes a leaf at its `fieldPath` (nested objects are
 *   created automatically).
 * - `pinnedEventTypes` lands at the top-level `eventType` key as an
 *   OR-of-values match. An empty array is treated as "match any event type"
 *   and is omitted from the pattern entirely.
 */
export function buildPattern(
  rows: FieldFilterRow[],
  pinnedEventTypes: string[] = [],
): RoutingPattern {
  const out: Record<string, unknown> = {};
  if (pinnedEventTypes.length > 0) {
    out[TOP_LEVEL_EVENT_TYPE_KEY] = [...pinnedEventTypes];
  }
  for (const row of rows) {
    if (!row.fieldPath) continue;
    // Keyed rows write one level below the field path. `setAtPath` reuses an
    // existing object at an intermediate key, so two keys on the same field
    // merge into one map — which is what makes them AND rather than replace
    // each other.
    const path = row.tagKey ? `${row.fieldPath}.${row.tagKey}` : row.fieldPath;
    setAtPath(out, path, leafForRow(row));
  }
  return out as RoutingPattern;
}

/**
 * Split a leaf path into (declared field, map key) when it sits one level below
 * a `keyed` field. Returns undefined for ordinary paths.
 */
function splitKeyedPath(
  path: string,
  fields: FilterField[],
): { fieldPath: string; tagKey: string } | undefined {
  const idx = path.lastIndexOf('.');
  if (idx <= 0) return undefined;
  const parent = path.slice(0, idx);
  const tagKey = path.slice(idx + 1);
  const field = fields.find((f) => f.path === parent);
  return field?.keyed ? { fieldPath: parent, tagKey } : undefined;
}

/**
 * Reverse of `buildPattern`. Returns the field rows + pinned event types
 * extracted from a pattern, plus an `unparseable` carryover for any leaves
 * we couldn't map back to a row (e.g. operators we don't model in the UI).
 *
 * `fields` is consulted to constrain which paths we recognise as fields —
 * any path not listed gets surfaced in `unparseable` so the user notices
 * rather than silently losing data.
 */
export function parsePattern(
  pattern: RoutingPattern,
  fields: FilterField[],
): {
  rows: FieldFilterRow[];
  pinnedEventTypes: string[];
  unparseable?: RoutingPattern;
} {
  const fieldPaths = new Set(fields.map((f) => f.path));
  const rows: FieldFilterRow[] = [];
  let pinnedEventTypes: string[] = [];
  const unparseable: Record<string, unknown> = {};

  // Pull the top-level eventType pin out first so its path doesn't collide.
  const evtRaw = (pattern as Record<string, unknown>)[TOP_LEVEL_EVENT_TYPE_KEY];
  if (Array.isArray(evtRaw)) {
    pinnedEventTypes = evtRaw.map(String);
  } else if (typeof evtRaw === 'string') {
    pinnedEventTypes = [evtRaw];
  } else if (evtRaw !== undefined) {
    unparseable[TOP_LEVEL_EVENT_TYPE_KEY] = evtRaw;
  }

  const leafPaths = collectLeafPaths(pattern as Record<string, unknown>).filter(
    (p) => p !== TOP_LEVEL_EVENT_TYPE_KEY,
  );

  for (const path of leafPaths) {
    const leaf = getAtPath(pattern as Record<string, unknown>, path);
    const row = leafToRow(path, leaf);
    if (!row) {
      setAtPath(unparseable, path, leaf as PatternValue);
      continue;
    }
    if (fieldPaths.has(path)) {
      rows.push(row);
      continue;
    }
    const keyed = splitKeyedPath(path, fields);
    if (keyed) {
      rows.push({ ...row, fieldPath: keyed.fieldPath, tagKey: keyed.tagKey });
      continue;
    }
    setAtPath(unparseable, path, leaf as PatternValue);
  }

  return Object.keys(unparseable).length > 0
    ? { rows, pinnedEventTypes, unparseable: unparseable as RoutingPattern }
    : { rows, pinnedEventTypes };
}

/**
 * Read-only counterpart to `parsePattern`. Decodes a pattern into displayable
 * rows + pinned event types WITHOUT a `FilterField[]` catalog — every leaf the
 * decoder understands becomes a row (the dot-path is self-descriptive). Only
 * leaves `leafToRow` can't model (operators we don't render) fall into
 * `unparseable`, so nothing silently disappears.
 *
 * Use this for displaying a stored rule; use `parsePattern` for the editor,
 * where the catalog constrains which paths are editable.
 */
export function summarizePattern(pattern: RoutingPattern): {
  rows: FieldFilterRow[];
  pinnedEventTypes: string[];
  unparseable?: RoutingPattern;
} {
  const rows: FieldFilterRow[] = [];
  let pinnedEventTypes: string[] = [];
  const unparseable: Record<string, unknown> = {};

  const evtRaw = (pattern as Record<string, unknown>)[TOP_LEVEL_EVENT_TYPE_KEY];
  if (Array.isArray(evtRaw)) {
    pinnedEventTypes = evtRaw.map(String);
  } else if (typeof evtRaw === 'string') {
    pinnedEventTypes = [evtRaw];
  } else if (evtRaw !== undefined) {
    unparseable[TOP_LEVEL_EVENT_TYPE_KEY] = evtRaw;
  }

  const leafPaths = collectLeafPaths(pattern as Record<string, unknown>).filter(
    (p) => p !== TOP_LEVEL_EVENT_TYPE_KEY,
  );

  for (const path of leafPaths) {
    const leaf = getAtPath(pattern as Record<string, unknown>, path);
    const row = leafToRow(path, leaf);
    if (row) {
      rows.push(row);
    } else {
      setAtPath(unparseable, path, leaf as PatternValue);
    }
  }

  return Object.keys(unparseable).length > 0
    ? { rows, pinnedEventTypes, unparseable: unparseable as RoutingPattern }
    : { rows, pinnedEventTypes };
}

/** Human-readable rendering of a row's value, paired with `OPERATOR_LABELS`. */
export function formatRowValue(row: FieldFilterRow): string {
  switch (row.operator) {
    case 'equals':
      return String(row.value ?? '');
    case 'one-of':
    case 'anything-but': {
      const arr = Array.isArray(row.value) ? row.value : [];
      return `[${arr.map(String).join(', ')}]`;
    }
    case 'prefix':
    case 'contains':
    case 'wildcard':
      return `"${String(row.value ?? '')}"`;
    case 'exists':
      return row.value === false ? 'does not exist' : 'exists';
    case 'numeric': {
      const v = (row.value ?? {}) as { op?: NumericComparator; n?: number };
      return `${v.op ?? '='} ${v.n ?? 0}`;
    }
  }
}

function leafToRow(path: string, leaf: unknown): FieldFilterRow | undefined {
  if (leaf == null) return undefined;

  if (Array.isArray(leaf)) {
    // Single-element array → equals; multi-element → one-of.
    if (leaf.length === 1) {
      return { id: newRowId(), fieldPath: path, operator: 'equals', value: leaf[0] };
    }
    return { id: newRowId(), fieldPath: path, operator: 'one-of', value: leaf };
  }

  if (typeof leaf === 'object') {
    const obj = leaf as Record<string, unknown>;
    if ('prefix' in obj) {
      return { id: newRowId(), fieldPath: path, operator: 'prefix', value: String(obj.prefix) };
    }
    if ('wildcard' in obj) {
      const raw = String(obj.wildcard);
      const inner = raw.slice(1, -1);
      // A `*x*` pattern with no other asterisk is what the `contains` operator
      // emits, so read it back as `contains`. Anything richer stays a raw
      // wildcard row rather than being flattened into a lossy `contains`.
      const isContains = raw.length > 2 && raw.startsWith('*') && raw.endsWith('*') && !inner.includes('*');
      return {
        id: newRowId(),
        fieldPath: path,
        operator: isContains ? 'contains' : 'wildcard',
        value: isContains ? inner : raw,
      };
    }
    if ('exists' in obj) {
      return { id: newRowId(), fieldPath: path, operator: 'exists', value: Boolean(obj.exists) };
    }
    if ('anything-but' in obj) {
      const arr = obj['anything-but'];
      return {
        id: newRowId(),
        fieldPath: path,
        operator: 'anything-but',
        value: Array.isArray(arr) ? arr : [arr],
      };
    }
    if ('numeric' in obj && Array.isArray(obj.numeric) && obj.numeric.length === 2) {
      const [op, n] = obj.numeric;
      return {
        id: newRowId(),
        fieldPath: path,
        operator: 'numeric',
        value: { op: op as NumericComparator, n: Number(n) },
      };
    }
    return undefined;
  }

  // Bare primitive at a leaf — treat as equals.
  return { id: newRowId(), fieldPath: path, operator: 'equals', value: leaf };
}

/**
 * Lightweight client-side validation. Server-side validation is authoritative;
 * this catches obvious mistakes before the request is sent.
 */
export function validateRow(
  row: FieldFilterRow,
  fields: FilterField[],
): string | undefined {
  const field = fields.find((f) => f.path === row.fieldPath);
  if (!field) return 'Pick a field';
  if (field.keyed && !row.tagKey) return `Enter a ${field.keyed.keyLabel.toLowerCase()}`;

  switch (row.operator) {
    case 'equals':
      if (row.value === '' || row.value == null) return 'Enter a value';
      return undefined;
    case 'one-of': {
      const arr = row.value as unknown[];
      if (!Array.isArray(arr) || arr.length === 0) return 'Add at least one value';
      return undefined;
    }
    case 'prefix':
      if (!row.value || typeof row.value !== 'string') return 'Enter a prefix';
      return undefined;
    case 'contains':
      if (!row.value || typeof row.value !== 'string') return 'Enter text to match';
      return undefined;
    case 'wildcard': {
      if (!row.value || typeof row.value !== 'string') return 'Enter a pattern';
      // The backend rejects consecutive '*' because EventBridge does.
      if (/(^|[^\\])\*\*/.test(row.value)) return "Use a single '*' between literals";
      return undefined;
    }
    case 'exists':
      // Boolean toggle — both true and false are valid.
      return undefined;
    case 'anything-but': {
      const arr = row.value as unknown[];
      if (!Array.isArray(arr) || arr.length === 0) return 'Add at least one value to exclude';
      return undefined;
    }
    case 'numeric': {
      const v = (row.value ?? {}) as { op?: NumericComparator; n?: number };
      if (typeof v.n !== 'number' || Number.isNaN(v.n)) return 'Enter a number';
      if (!v.op) return 'Pick a comparator';
      if (field.type !== 'number') return 'Numeric operators only work on number fields';
      return undefined;
    }
  }
}

/** Operators allowed for a given field type. Drives the operator dropdown. */
export function operatorsForType(type: FilterField['type']): PatternOperator[] {
  switch (type) {
    case 'string':
      return ['equals', 'one-of', 'contains', 'prefix', 'wildcard', 'exists', 'anything-but'];
    case 'number':
      return ['equals', 'one-of', 'numeric', 'exists', 'anything-but'];
    case 'boolean':
      return ['equals', 'exists'];
  }
}

export const OPERATOR_LABELS: Record<PatternOperator, string> = {
  equals: 'equals',
  'one-of': 'is one of',
  prefix: 'starts with',
  contains: 'contains',
  wildcard: 'matches pattern',
  exists: 'exists',
  'anything-but': 'is not',
  numeric: 'numeric',
};
